import type { InstantRoomBlockGuestView } from './instantRoomBlockGuestView.types';

/**
 * Path builders for the guest's instant room block pages under the event handle.
 *
 * The guest app may not import from `@apps/admin`, so the guest side builds its own paths. The booking
 * and checkout paths already start with `/${eventHandle}`; the hotel page paths are root-relative. Callers
 * use each path as it is and do not prefix it with `buildPath`.
 */

/** The path segment after the event handle. `CARBON_KEPT_GUEST_SUBPATHS` (`src/server/utils/carbonGuestSite.ts`) lists it too. */
export const INSTANT_ROOM_BLOCK_BOOKING_PATH_SEGMENT = 'instant-room-block';

/** The route pattern `Guest.tsx` registers. This pattern plus `/checkout` is the old claim checkout's path, which now redirects. */
export const INSTANT_ROOM_BLOCK_GUEST_ROUTE_PATH = `/:eventHandle/${INSTANT_ROOM_BLOCK_BOOKING_PATH_SEGMENT}/:instantRoomBlockId`;

/** The booking link page: `/${eventHandle}/instant-room-block/${instantRoomBlockId}`. */
export const buildInstantRoomBlockBookingLinkPath = (eventHandle: string, instantRoomBlockId: string): string =>
  `/${encodeURIComponent(eventHandle)}/${INSTANT_ROOM_BLOCK_BOOKING_PATH_SEGMENT}/${encodeURIComponent(instantRoomBlockId)}`;

/** The hotel's stays page: `/stays/${joyPlaceId}?eventId=${eventId}` (requires Postgres eventId). */
export const buildInstantRoomBlockHotelPagePath = (joyPlaceId: string, eventId: string): string =>
  `/stays/${encodeURIComponent(joyPlaceId)}?eventId=${encodeURIComponent(eventId)}`;

/** The URL param that puts the hotel's stays page, and the hotel checkout, in room block mode. */
export const INSTANT_ROOM_BLOCK_ID_PARAM = 'instantRoomBlockId';

/** The checkout URL param with the claim's hold deadline, an ISO UTC datetime. */
export const INSTANT_ROOM_BLOCK_CLAIM_EXPIRES_AT_PARAM = 'claimExpiresAt';

// Stripe's own param name. The hotel checkout reads the client secret from it.
const STRIPE_CLIENT_SECRET_PARAM = 'payment_intent_client_secret';

/** Older blocks have no adults count; the stays page reads a missing count as two adults too. */
export const DEFAULT_INSTANT_ROOM_BLOCK_ADULTS = 2;

type InstantRoomBlockStayDates = Pick<InstantRoomBlockGuestView, 'checkInDate' | 'checkOutDate'>;

/** The block's dates in the params the stays page reads. */
export const buildInstantRoomBlockStayDateParams = (stay: InstantRoomBlockStayDates): Readonly<{ checkin: string; checkout: string }> => ({
  checkin: stay.checkInDate,
  checkout: stay.checkOutDate,
});

type InstantRoomBlockStay = InstantRoomBlockStayDates & Pick<InstantRoomBlockGuestView, 'adults'>;

/** The block's stay in the params the stays page reads. `rooms=a2` is one room for two adults. */
export const buildInstantRoomBlockStayParams = (stay: InstantRoomBlockStay): Readonly<{ checkin: string; checkout: string; rooms: string }> => ({
  ...buildInstantRoomBlockStayDateParams(stay),
  rooms: `a${stay.adults ?? DEFAULT_INSTANT_ROOM_BLOCK_ADULTS}`,
});

type InstantRoomBlockHotelStay = InstantRoomBlockStay & Pick<InstantRoomBlockGuestView, 'joyPlaceId' | 'instantRoomBlockId'>;

/** The hotel's stays page in room block mode, for the block's stay. */
export const buildInstantRoomBlockPdpPath = (guestView: InstantRoomBlockHotelStay, eventId: string): string => {
  const searchParams = new URLSearchParams({ eventId, [INSTANT_ROOM_BLOCK_ID_PARAM]: guestView.instantRoomBlockId, ...buildInstantRoomBlockStayParams(guestView) });
  return `/stays/${encodeURIComponent(guestView.joyPlaceId)}?${searchParams.toString()}`;
};

type InstantRoomBlockShareLink = InstantRoomBlockStayDates & Pick<InstantRoomBlockGuestView, 'joyPlaceId' | 'instantRoomBlockId'>;

/**
 * The link the host copies and sends: the hotel's stays page in room block mode, for the block's dates.
 * It carries no `rooms`: the host's block read has no adults count, and the page adds the block's own.
 */
export const buildInstantRoomBlockShareLinkPath = (block: InstantRoomBlockShareLink, eventId: string): string => {
  const searchParams = new URLSearchParams({ eventId, [INSTANT_ROOM_BLOCK_ID_PARAM]: block.instantRoomBlockId, ...buildInstantRoomBlockStayDateParams(block) });
  return `/stays/${encodeURIComponent(block.joyPlaceId)}?${searchParams.toString()}`;
};

/** The hotel's stays page with live rates for the block's stay: the room block mode link without the block. */
export const buildInstantRoomBlockSeeAllRoomsPath = (guestView: InstantRoomBlockHotelStay, eventId: string): string => {
  const searchParams = new URLSearchParams({ eventId, ...buildInstantRoomBlockStayParams(guestView) });
  return `/stays/${encodeURIComponent(guestView.joyPlaceId)}?${searchParams.toString()}`;
};

/** A claim that has started: the room is held for this guest until `claimExpiresAt`. */
type StartedInstantRoomBlockClaim = Readonly<{ reservationId: string; paymentIntentClientSecret: string; claimExpiresAt: string }>;

type InstantRoomBlockCheckoutBlock = Pick<InstantRoomBlockGuestView, 'instantRoomBlockId' | 'joyPlaceId'>;

/**
 * The hotel checkout in room block mode, for a claim that has started. It carries no `offerToken`: the
 * checkout reads the claim's reservation by id, and a URL `offerToken` that differs would stall it.
 * `returnTo` brings the guest back to the hotel's stays page; without it the checkout goes back in history.
 */
export const buildInstantRoomBlockCheckoutPath = (claim: StartedInstantRoomBlockClaim, block: InstantRoomBlockCheckoutBlock, eventId: string, returnTo?: string): string => {
  const searchParams = new URLSearchParams({
    [INSTANT_ROOM_BLOCK_ID_PARAM]: block.instantRoomBlockId,
    reservationId: claim.reservationId,
    [STRIPE_CLIENT_SECRET_PARAM]: claim.paymentIntentClientSecret,
    [INSTANT_ROOM_BLOCK_CLAIM_EXPIRES_AT_PARAM]: claim.claimExpiresAt,
    eventId,
    joyPlaceId: block.joyPlaceId,
    ...(returnTo ? { returnTo } : {}),
  });
  return `/travel/checkout?${searchParams.toString()}`;
};
